import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiArrowUp } from 'react-icons/fi';
import { Message } from '../../../types/chat';
import MessageItem from './MessageItem';
import { InlineSpinner } from './InlineSpinner';

interface MessageListProps {
  messages: Message[];
  loadingMore: boolean;
  hasMoreMessages: boolean;
  loadMoreMessages: () => void;
  isSending: boolean;
  conversationLoading: boolean;
  selectedConversationId: string | null;
  emptyStateContent?: React.ReactNode;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  loadingMore,
  hasMoreMessages,
  loadMoreMessages,
  isSending,
  conversationLoading,
  selectedConversationId,
  emptyStateContent,
}) => {
  const { t } = useTranslation(['v0/chat', 'v0/common']);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messageListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (messageListRef.current) {
      const scrollThreshold = 100;
      const isNearBottom = messageListRef.current.scrollHeight - messageListRef.current.scrollTop - messageListRef.current.clientHeight < scrollThreshold;

      if (isNearBottom || isSending) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages, isSending]);

  return (
    <div ref={messageListRef} className="flex-1 overflow-y-auto px-4 pt-4 pb-2 bg-bg-secondary dark:bg-bg-dark-secondary">
      {hasMoreMessages && (
        <div className="text-center mb-4">
          <button
            type="button"
            onClick={loadMoreMessages}
            disabled={loadingMore}
            className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loadingMore ? (
              <>
                <InlineSpinner className="w-4 h-4 mr-1" /> {t('v0/common:loadingText')}...
              </>
            ) : (
              <>
                <FiArrowUp className="w-4 h-4 mr-1" /> {t('v0/chat:loadMore')}
              </>
            )}
          </button>
        </div>
      )}

      {messages.map((msg, index) => (
        <MessageItem
          key={msg.metadata?.stableKey || msg.id || `temp-${index}`}
          message={msg}
          index={index}
          totalMessages={messages.length}
        />
      ))}

      {!conversationLoading && messages.length === 0 && (
        <div className="flex items-center justify-center h-full text-center text-gray-500 dark:text-gray-400">
          {emptyStateContent || (
            <p>{selectedConversationId ? t('v0/chat:startTyping') : t('v0/chat:selectOrCreateConversation')}</p>
          )}
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
};